import { Mail, Phone, Save } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { formatCurrencyFromCents } from '../../data/protection-catalog'
import { updateAdminBooking } from '../../lib/admin-command'
import {
  bookingAddOnSummary,
  bookingCustomerEmail,
  bookingCustomerName,
  bookingCustomerPhone,
  bookingListStatus,
  bookingPackage,
  bookingVehicleLabel,
  formatAppointment,
  isTestBooking,
  paidDepositCents,
  xeroStatusLabel,
  type AdminCanonicalBooking,
} from '../../lib/admin-workspace'
import { ymdInTimeZone } from '../../lib/appointment-scheduling'
import { callAuthedSupabaseFunction, callSupabaseFunction } from '../../lib/api'
import { CERAMIC_BOOKING_DEPOSIT_CENTS } from '../../lib/booking-domain'
import { formatAudCurrency } from '../../lib/service-catalog'
import { supabase } from '../../lib/supabase'
import { canLaunchSquareFromAdmin, type PublicSquareConnection } from '../../lib/square-oauth'
import { canCreateXeroInvoiceFromAdmin, type PublicXeroConnection } from '../../lib/xero-oauth'
import { buildSettlementSnapshot } from '../../lib/job-settlement'

function timeInZone(startAt: string | null, timeZone: string) {
  if (!startAt) return ''
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(startAt))
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '00'
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '00'
  return `${hour === '24' ? '00' : hour}:${minute}`
}

export function AdminBookingDetail({
  booking,
  xeroConnection,
  squareConnection,
  editing,
  onEditingChange,
  onChanged,
  onRequestCancel,
  onMessage,
}: Readonly<{
  booking: AdminCanonicalBooking
  xeroConnection: PublicXeroConnection | null
  squareConnection: PublicSquareConnection | null
  editing: boolean
  onEditingChange: (editing: boolean) => void
  onChanged: () => Promise<void> | void
  onRequestCancel: () => void
  onMessage: (message: string | null, error?: boolean) => void
}>) {
  const timezone = booking.appointment_timezone || 'Australia/Melbourne'
  const canCreateInvoice = canCreateXeroInvoiceFromAdmin(xeroConnection)
  const squareReady = canLaunchSquareFromAdmin(squareConnection)
  const [busy, setBusy] = useState(false)
  const [finalAmountAud, setFinalAmountAud] = useState('')
  const [invoiceDescription, setInvoiceDescription] = useState('')
  const [xeroBusy, setXeroBusy] = useState(false)
  const [slots, setSlots] = useState<Array<{ time: string; startAt: string; available: boolean }>>([])
  const [form, setForm] = useState({
    customerName: bookingCustomerName(booking),
    customerEmail: bookingCustomerEmail(booking) ?? '',
    customerPhone: bookingCustomerPhone(booking) ?? '',
    vehicleMake: booking.vehicle_make ?? '',
    vehicleModel: booking.vehicle_model ?? '',
    internalNotes: booking.internal_notes ?? '',
    appointmentDate: booking.start_at ? ymdInTimeZone(new Date(booking.start_at), timezone) : '',
    appointmentTime: timeInZone(booking.start_at, timezone),
  })

  useEffect(() => {
    setForm({
      customerName: bookingCustomerName(booking),
      customerEmail: bookingCustomerEmail(booking) ?? '',
      customerPhone: bookingCustomerPhone(booking) ?? '',
      vehicleMake: booking.vehicle_make ?? '',
      vehicleModel: booking.vehicle_model ?? '',
      internalNotes: booking.internal_notes ?? '',
      appointmentDate: booking.start_at ? ymdInTimeZone(new Date(booking.start_at), timezone) : '',
      appointmentTime: timeInZone(booking.start_at, timezone),
    })
  }, [booking, timezone])

  useEffect(() => {
    if (!editing || !form.appointmentDate) {
      setSlots([])
      return
    }
    let cancelled = false
    void callSupabaseFunction('booking-availability', undefined, {
      reference: booking.public_reference,
      date: form.appointmentDate,
    }).then(async (response) => {
      const payload = await response.json() as { slots?: Array<{ time: string; startAt: string; available: boolean }> }
      if (!cancelled) setSlots(payload.slots ?? [])
    }).catch(() => {
      if (!cancelled) setSlots([])
    })
    return () => {
      cancelled = true
    }
  }, [booking.public_reference, editing, form.appointmentDate])

  async function saveDetails(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    onMessage(null)
    try {
      await updateAdminBooking({
        bookingId: booking.id,
        ...form,
      })
      onEditingChange(false)
      onMessage('Booking details updated.')
      await onChanged()
    } catch (error) {
      onMessage(error instanceof Error ? error.message : 'Unable to update booking.', true)
    } finally {
      setBusy(false)
    }
  }

  async function createFinalInvoice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canCreateInvoice) {
      onMessage('Connect Xero before creating a final invoice.', true)
      return
    }
    setXeroBusy(true)
    onMessage(null)
    try {
      const { data } = await supabase.auth.getSession()
      const accessToken = data.session?.access_token
      if (!accessToken) {
        onMessage('Sign in again to create a Xero invoice.', true)
        return
      }
      const response = await callAuthedSupabaseFunction('booking-job-complete', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingId: booking.id,
          finalAmountAud,
          description: invoiceDescription,
        }),
      })
      const payload = await response.json() as {
        ok?: boolean
        error?: string
        invoiceNumber?: string
        outstandingCents?: number
        depositAppliedCents?: number
        invoiceTotalCents?: number
        instructions?: string
        jobFinalised?: boolean
        invoiced?: boolean
      }
      if (payload.jobFinalised && !payload.invoiced) {
        onMessage(payload.error || payload.instructions || 'Xero is not connected.', true)
        await onChanged()
        return
      }
      if (!payload.ok) {
        onMessage(payload.instructions || payload.error || 'Xero invoice was not created.', true)
        return
      }
      onMessage(
        `Xero ${payload.invoiceNumber}: ${formatCurrencyFromCents(payload.invoiceTotalCents ?? 0)} invoice, ${formatCurrencyFromCents(payload.depositAppliedCents ?? CERAMIC_BOOKING_DEPOSIT_CENTS)} deposit applied, ${formatCurrencyFromCents(payload.outstandingCents ?? 0)} remaining.`,
      )
      await onChanged()
    } catch (error) {
      onMessage(error instanceof Error ? error.message : 'Xero invoice was not created.', true)
    } finally {
      setXeroBusy(false)
    }
  }

  const cancelled = booking.status === 'cancelled' || booking.appointment_status === 'cancelled'
  const xero = booking.xero_sync_records?.[0]
  const settlement = buildSettlementSnapshot({
    jobStatus: booking.job_status,
    finalJobAmountCents: booking.final_job_amount_cents,
    stripePaymentStatus: booking.payment_status,
    depositPaidCents: paidDepositCents(booking) || booking.deposit_amount_cents,
    invoiceId: xero?.invoice_id,
    invoiceNumber: xero?.invoice_number,
    invoiceTotalCents: xero?.invoice_total_cents,
    amountPaidCents: xero?.amount_paid_cents,
    outstandingCents: xero?.outstanding_cents,
    onlineInvoiceUrl: xero?.online_invoice_url,
    squareConnected: squareReady,
    settlementSource: xero?.settlement_source,
    failed: xero?.sync_status === 'failed',
  })

  async function authedJson(name: string, body: Record<string, unknown>) {
    const { data } = await supabase.auth.getSession()
    const accessToken = data.session?.access_token
    if (!accessToken) throw new Error('Sign in again.')
    const response = await callAuthedSupabaseFunction(name, accessToken, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    return await response.json() as Record<string, unknown>
  }

  async function takeSquarePayment() {
    setXeroBusy(true)
    onMessage(null)
    try {
      const payload = await authedJson('booking-square-checkout', { bookingId: booking.id })
      if (!payload.ok) {
        onMessage(String(payload.error || 'Square payment could not start.'), true)
        return
      }
      const iosUrl = typeof payload.iosUrl === 'string' ? payload.iosUrl : ''
      if (iosUrl) window.location.assign(iosUrl)
      onMessage(`Take remaining ${formatCurrencyFromCents(Number(payload.amountCents) || 0)} in Square Point of Sale.`)
    } catch (error) {
      onMessage(error instanceof Error ? error.message : 'Square payment could not start.', true)
    } finally {
      setXeroBusy(false)
    }
  }

  async function refreshXeroSettlement() {
    setXeroBusy(true)
    onMessage(null)
    try {
      const payload = await authedJson('booking-settlement-refresh', { bookingId: booking.id })
      if (!payload.ok) {
        onMessage(String(payload.error || 'Xero invoice status was not refreshed.'), true)
        return
      }
      onMessage('Xero invoice status refreshed.')
      await onChanged()
    } catch (error) {
      onMessage(error instanceof Error ? error.message : 'Xero invoice status was not refreshed.', true)
    } finally {
      setXeroBusy(false)
    }
  }

  return (
    <>
      <div className="admin-card-heading">
        <div>
          <h2>{booking.public_reference}</h2>
          <p>{bookingCustomerName(booking)} - {bookingVehicleLabel(booking)}</p>
        </div>
        <span className="status-pill">{bookingListStatus(booking)}</span>
        {isTestBooking(booking) ? <span className="status-pill test-data-pill">TEST</span> : null}
      </div>
      <div className="booking-contact-actions">
        {bookingCustomerPhone(booking) ? <a href={`tel:${bookingCustomerPhone(booking)}`}><Phone size={15} /> Call</a> : null}
        {bookingCustomerEmail(booking) ? <a href={`mailto:${bookingCustomerEmail(booking)}?subject=Autoglow booking ${booking.public_reference}`}><Mail size={15} /> Email</a> : null}
        <button type="button" onClick={() => onEditingChange(true)}>Edit</button>
        {!cancelled ? <button type="button" onClick={onRequestCancel}>Cancel booking</button> : null}
      </div>
      <dl className="review-list">
        <div><dt>Customer</dt><dd>{bookingCustomerName(booking)}</dd></div>
        <div><dt>Email</dt><dd>{bookingCustomerEmail(booking) || 'Not provided'}</dd></div>
        <div><dt>Phone</dt><dd>{bookingCustomerPhone(booking) || 'Not provided'}</dd></div>
        <div><dt>Vehicle</dt><dd>{bookingVehicleLabel(booking)}</dd></div>
        <div><dt>Protection</dt><dd>{bookingPackage(booking).displayName} - {bookingPackage(booking).productName}</dd></div>
        <div><dt>Service From price</dt><dd>{formatAudCurrency(booking.authoritative_starting_price_cents ?? bookingPackage(booking).startingPriceCents)} starting, not final revenue</dd></div>
        <div><dt>Job status</dt><dd>{booking.job_status || 'booked'}</dd></div>
        <div><dt>Final job amount</dt><dd>{booking.final_job_amount_cents != null ? formatCurrencyFromCents(booking.final_job_amount_cents) : 'Not finalised'}</dd></div>
        <div><dt>Work performed summary</dt><dd>{booking.work_performed_summary || 'Not recorded'}</dd></div>
        <div><dt>Deposit</dt><dd>{formatCurrencyFromCents(settlement.depositPaidCents)} / {booking.payment_status}</dd></div>
        <div><dt>Remaining balance</dt><dd>{formatCurrencyFromCents(settlement.remainingCents)}</dd></div>
        <div><dt>Xero invoice status</dt><dd>{xeroStatusLabel(booking)}{xero?.settlement_status ? ` · ${xero.settlement_status}` : ''}</dd></div>
        <div><dt>Square payment</dt><dd>{squareReady ? (squareConnection?.locationName || 'Connected') : (squareConnection?.status === 'setup_required' ? 'Not connected / not configured' : 'Not connected')}</dd></div>
        <div><dt>Appointment</dt><dd>{formatAppointment(booking.start_at, timezone)}</dd></div>
        <div><dt>Appointment status</dt><dd>{booking.appointment_status || 'not_selected'}</dd></div>
        <div><dt>Booking status</dt><dd>{booking.status} / {bookingListStatus(booking)}</dd></div>
        <div><dt>Add-ons</dt><dd>{bookingAddOnSummary(booking.booking_add_ons)}</dd></div>
        <div><dt>Manual confirmation</dt><dd>{booking.requires_manual_confirmation ? 'Required' : 'Not required'}</dd></div>
        <div><dt>Stripe Checkout</dt><dd>{booking.stripe_checkout_session_id || booking.booking_payments?.[0]?.provider_checkout_session_id || 'Not available'}</dd></div>
        <div><dt>Stripe Payment Intent</dt><dd>{booking.stripe_payment_intent_id || booking.booking_payments?.[0]?.provider_payment_intent_id || 'Not available'}</dd></div>
        <div><dt>Xero</dt><dd>{xeroStatusLabel(booking)}</dd></div>
        {booking.xero_sync_records?.[0]?.outstanding_cents != null ? (
          <div><dt>Xero remaining</dt><dd>{formatCurrencyFromCents(booking.xero_sync_records[0].outstanding_cents)}</dd></div>
        ) : null}
        <div><dt>Customer notes</dt><dd>{booking.customer_notes || 'None'}</dd></div>
        <div><dt>Internal notes</dt><dd>{booking.internal_notes || 'None'}</dd></div>
      </dl>

      {editing ? (
        <form className="admin-edit-grid" onSubmit={(event) => void saveDetails(event)}>
          <label>Customer name<input required value={form.customerName} onChange={(event) => setForm((current) => ({ ...current, customerName: event.target.value }))} /></label>
          <label>Email<input required type="email" value={form.customerEmail} onChange={(event) => setForm((current) => ({ ...current, customerEmail: event.target.value }))} /></label>
          <label>Phone<input required value={form.customerPhone} onChange={(event) => setForm((current) => ({ ...current, customerPhone: event.target.value }))} /></label>
          <label>Vehicle make<input required value={form.vehicleMake} onChange={(event) => setForm((current) => ({ ...current, vehicleMake: event.target.value }))} /></label>
          <label>Vehicle model<input required value={form.vehicleModel} onChange={(event) => setForm((current) => ({ ...current, vehicleModel: event.target.value }))} /></label>
          <label>Appointment date<input type="date" value={form.appointmentDate} onChange={(event) => setForm((current) => ({ ...current, appointmentDate: event.target.value }))} /></label>
          <label>
            Appointment time
            {slots.length > 0 ? (
              <select value={form.appointmentTime} onChange={(event) => setForm((current) => ({ ...current, appointmentTime: event.target.value }))}>
                <option value="">Keep current / choose a slot</option>
                {slots.filter((slot) => slot.available || slot.time === form.appointmentTime).map((slot) => (
                  <option key={slot.startAt} value={slot.time}>{slot.time}{slot.available ? '' : ' (current)'}</option>
                ))}
              </select>
            ) : (
              <input type="time" value={form.appointmentTime} onChange={(event) => setForm((current) => ({ ...current, appointmentTime: event.target.value }))} />
            )}
          </label>
          <label className="wide">Internal notes<textarea rows={5} value={form.internalNotes} onChange={(event) => setForm((current) => ({ ...current, internalNotes: event.target.value }))} /></label>
          <p className="admin-empty-state wide">Payment amount, payment status, Stripe IDs and the booking reference cannot be edited here.</p>
          <div className="admin-inline-actions wide">
            <button disabled={busy} type="submit"><Save size={15} /> Save changes</button>
            <button type="button" onClick={() => onEditingChange(false)}>Cancel edit</button>
          </div>
        </form>
      ) : null}

      {booking.payment_status === 'paid' ? (
        <form className="admin-edit-grid" onSubmit={(event) => void createFinalInvoice(event)}>
          <label>
            Final job amount (AUD)
            <input
              inputMode="decimal"
              onChange={(event) => setFinalAmountAud(event.target.value)}
              placeholder="1650.00"
              required
              value={finalAmountAud}
            />
          </label>
          <label className="wide">
            Work performed summary
            <input
              onChange={(event) => setInvoiceDescription(event.target.value)}
              placeholder="Ceramic protection after inspection"
              value={invoiceDescription}
            />
          </label>
          <button disabled={xeroBusy} type="submit">
            {canCreateInvoice ? 'Mark Job Complete' : 'Finalise Job'}
          </button>
          {settlement.remainingCents > 0 && xero?.invoice_id ? (
            <button disabled={xeroBusy || !squareReady} onClick={() => void takeSquarePayment()} type="button">
              Take Remaining Payment
            </button>
          ) : null}
          {xero?.invoice_id ? (
            <button disabled={xeroBusy || !canCreateInvoice} onClick={() => void refreshXeroSettlement()} type="button">
              Refresh Xero invoice
            </button>
          ) : null}
          {xero?.online_invoice_url ? (
            <a href={xero.online_invoice_url} rel="noreferrer" target="_blank">Open Xero Invoice</a>
          ) : null}
          {!canCreateInvoice ? (
            <p className="admin-empty-state">
              Job amount can be saved now. Xero is not connected, so a full-value invoice will not be created.
              Connect a Demo org and select sales, Stripe clearing, and Square clearing BANK accounts first.
              Live organisation writes stay blocked until they are explicitly allowed.
            </p>
          ) : null}
          {!squareReady ? (
            <p className="admin-empty-state">
              Square remaining-balance collection is disabled until Square is configured, connected,
              and a Reader location is selected.
            </p>
          ) : null}
        </form>
      ) : (
        <p className="admin-empty-state">The Stripe deposit must be paid before a Xero invoice can be created.</p>
      )}
    </>
  )
}
